import React from 'react';

import { SettingContentCard } from './SettingContentCard';
import { SettingSectionHeader } from './SettingSectionHeader';

export interface SettingSectionProps {
  /** Section title */
  title: string;
  /** Optional description */
  description?: string;
  /** Optional tooltip */
  tooltip?: string;
  /** Section content */
  children: React.ReactNode;
  /** Optional action button (shown to the right of header) */
  action?: React.ReactNode;
  /** Card padding size */
  cardPadding?: 'sm' | 'md' | 'lg';
  /** Additional className for header */
  headerClassName?: string;
  /** Additional className for card */
  cardClassName?: string;
  /** If true, only renders header without card wrapper */
  headerOnly?: boolean;
}

/**
 * Complete settings section composition with header and card
 *
 * Combines SettingSectionHeader and SettingContentCard for consistent section layout.
 * Supports optional action button positioned next to header.
 *
 * @example
 * ```tsx
 * // Simple section with card
 * <SettingSection title="General Settings" description="Configure general options">
 *   <div>Settings form here</div>
 * </SettingSection>
 *
 * // With action button
 * <SettingSection
 *   title="API Keys"
 *   action={<Button label="Add Key" onClick={handleAdd} />}
 * >
 *   <APIKeyList />
 * </SettingSection>
 *
 * // Header only mode (no card wrapper)
 * <SettingSection title="Advanced" headerOnly>
 *   <CustomLayout />
 * </SettingSection>
 * ```
 */
export const SettingSection: React.FC<SettingSectionProps> = ({
  title,
  description,
  tooltip,
  children,
  action,
  cardPadding = 'md',
  headerClassName,
  cardClassName,
  headerOnly = false,
}) => {
  return (
    <div className="flex flex-col gap-6">
      {action ? (
        <div className="flex items-center justify-between">
          <SettingSectionHeader
            title={title}
            description={description}
            tooltip={tooltip}
            className={headerClassName}
          />
          {action}
        </div>
      ) : (
        <SettingSectionHeader
          title={title}
          description={description}
          tooltip={tooltip}
          className={headerClassName}
        />
      )}

      {!headerOnly && (
        <SettingContentCard padding={cardPadding} className={cardClassName}>
          {children}
        </SettingContentCard>
      )}

      {headerOnly && children}
    </div>
  );
};
